import { cva } from "class-variance-authority"
import Button from "./Button.svelte"

export type { ButtonProps } from "./Button.svelte"
export { Button }

const variant = {
  primary:
    "bg-button-blue text-icon-general-inverted hover:bg-button-blue-hovered active:bg-button-blue-hovered",
  secondary:
    "bg-button-primary text-button-primary hover:bg-button-primary-hovered active:bg-button-primary-hovered",
  success:
    "bg-button-green text-icon-general-inverted hover:bg-button-green-hovered active:bg-button-green-hovered",
  error:
    "bg-button-red text-icon-general-inverted hover:bg-button-red-hovered active:bg-button-red-hovered",
  notice:
    "bg-button-orange text-icon-general-inverted hover:bg-button-orange-hovered active:bg-button-orange-hovered",
  pending:
    "bg-button-gold text-icon-general-inverted hover:bg-button-gold-hovered active:bg-button-gold-hovered",
  ghostPrimary:
    "hover:bg-button-primary-hovered active:bg-button-primary-hovered hover:text-accent-foreground active:text-accent-foreground disabled:bg-transparent text-icon-general-primary",
  ghostSecondary:
    "hover:bg-button-primary-hovered active:bg-button-primary-hovered hover:text-accent-foreground active:text-accent-foreground disabled:bg-transparent text-icon-general-secondary",
  // FIXME: keyboard focus + active state shows no underline, so user receives no interaction feedback
  link: "[disabled]:text-button-inactive text-icon-general-blue focus-visible:underline hover:underline underline-offset-[3px] active:underline active:scale-100 focus-visible:active:underline disabled:bg-transparent",
}

const PSEUDO_BUTTON_VARIANTS = [
  "ghostPrimary",
  "ghostSecondary",
  "link",
] satisfies (keyof typeof variant)[]

const size = {
  small: "font-small-400 px-2.5 py-1.5",
  medium: "font-medium-400 px-3 py-1.5",
  large: "font-large-400 px-4 py-2",
}

// Icon slots. The box is sized per `size` and any slotted svg fills it, so the
// Button owns icon sizing rather than each call site. These defaults must track
// Button's own `size` default.
export const buttonIconVariants = cva(
  "inline-flex shrink-0 items-center justify-center [&_svg]:size-full",
  {
    variants: {
      size: {
        small: "size-4",
        medium: "size-5",
        large: "size-6",
      },
    },
    defaultVariants: {
      size: "large",
    },
  }
)

// Gap between an icon slot and the content. Applied only when a slot is filled,
// so icon-less buttons keep their existing spacing.
export const buttonIconGapVariants = cva("", {
  variants: {
    size: {
      small: "gap-x-1",
      medium: "gap-x-1.5",
      large: "gap-x-1.5",
    },
  },
  defaultVariants: {
    size: "large",
  },
})

export const buttonVariantKeys = Object.keys(
  variant
) as (keyof typeof variant)[]
export const buttonSizeKeys = Object.keys(size) as (keyof typeof size)[]

// NOTE: `focus-visible:outline-none` is load-bearing, not redundant. It emits
// `outline: 2px solid transparent`, which is what supplies `outline-style`;
// `outline-1` only sets the width and the token only sets the colour. Drop it
// and the focus ring disappears entirely (WCAG 2.4.7).
export const buttonVariants = cva(
  `inline-flex items-center justify-center rounded border border-semantic-transparent transition-[background-color,transform,translate,scale,rotate] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] focus-visible:outline-none focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-semantic-interaction-selected active:scale-95 disabled:pointer-events-none disabled:bg-button-disabled disabled:text-icon-general-disabled`,
  {
    variants: {
      variant,
      size,
    },
    compoundVariants: [
      {
        variant: PSEUDO_BUTTON_VARIANTS,
        // border-0 too: the base's transparent border is 2px of invisible
        // height on an intrinsically sized element, enough to push one of
        // these out of a fixed-height container (DateInput in an `h-10` field).
        className: "h-fit w-fit min-w-fit border-0 p-0",
      },
    ],
  }
)
